import {Injectable} from '@angular/core';
import {TermsConditionsService} from '@app/services/terms-conditions.service';
import {DcpGlobalMessageService} from '@dcp/configurator';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {of} from 'rxjs';
import {catchError, concatMap, filter, map, mergeMap, tap, withLatestFrom} from 'rxjs/operators';
import {TermsAndConditionsActions} from '../actions/terms-conditions.actions';
import {TermsAndConditionsAppState} from '../reducers/terms-conditions.reducer';
import {fromTermsAndConditions} from '../selectors/terms-conditions.selectors';

@Injectable()
export class TermsConditionsEffects {
  getTermsAndConditions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TermsAndConditionsActions.get),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(fromTermsAndConditions.selectByCanonicalId(action.canonicalId)).pipe(
              mergeMap((value) => {
                if (value === undefined) {
                  this.store.dispatch(TermsAndConditionsActions.initMap({canonicalId: action.canonicalId}));
                  return this.store.select(fromTermsAndConditions.selectByCanonicalId(action.canonicalId));
                } else if (action?.payload?.contractData?.contractId && value?.loadStatus === 'LOADED') {
                  this.store.dispatch(TermsAndConditionsActions.resetMap({canonicalId: action.canonicalId}));
                  return this.store.select(fromTermsAndConditions.selectByCanonicalId(action.canonicalId));
                }
                return of(value);
              })
            )
          )
        )
      ),
      filter(([, curr]) => curr?.loadStatus === 'NOT_LOADED'),
      map(([action]) => TermsAndConditionsActions.load({canonicalId: action.canonicalId, payload: action.payload}))
    )
  );

  loadTermsAndConditions$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(TermsAndConditionsActions.load),
        tap((action) => {
          this.store.dispatch(TermsAndConditionsActions.setLoadingState({canonicalId: action.canonicalId}));
        })
      )
      .pipe(
        mergeMap((action) =>
          this.termsConditionsService.fetchTermsConditionsByCanonicalId(action.canonicalId, action.payload).pipe(
            map((termsAndConditions) =>
              TermsAndConditionsActions.loaded({
                termsAndConditions,
                canonicalId: action.canonicalId,
              })
            ),
            catchError((error) => {
              this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
              return of(
                TermsAndConditionsActions.loadFailure({
                  error,
                  canonicalId: action.canonicalId,
                })
              );
            })
          )
        )
      )
  );

  constructor(
    private actions$: Actions,
    private termsConditionsService: TermsConditionsService,
    private store: Store<TermsAndConditionsAppState>,
    private dcpGlobalMessageService: DcpGlobalMessageService
  ) {}
}
